/* eslint-disable @ngrx/avoid-dispatching-multiple-actions-sequentially */
import { Location } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { ModalController, PopoverController } from '@ionic/angular';
import { Store } from '@ngrx/store';
import { Observable } from 'rxjs';
import { filter, first, map, tap } from 'rxjs/operators';

import { ProfileService } from 'src/app/pages/app/profile/services/profile.service';
import { RudderStack } from 'src/app/services';
import { CohortSelectorModalComponent } from '../cohort-selector-modal/cohort-selector-modal.component';
import {
	loadSidebars,
	menuChange,
	sidebarMenuChange,
} from './redux/sidebar.actions';
import { SideMenus } from './redux/sidebar.reducer';
import { selectSidebar, selectSidebarMenus } from './redux/sidebar.selectors';

@Component({
	selector: 'app-sidebar',
	templateUrl: './sidebar.component.html',
	styleUrls: ['./sidebar.component.scss'],
})
export class SidebarComponent implements OnInit {
	sideMenus: any = {
		highLights: 'highlights',
		mySchedule: 'my-schedule',
		assignments: 'assignments',
		courseInfo: 'course-info',
		resources: 'resources',
		myNotes: 'my-notes',
	};
	selected: any;

	sidebar$: Observable<any> = this.store.select(selectSidebar);

	sidebarMenu$: Observable<any> = this.store.select(selectSidebarMenus);

	profile$: Observable<any>;

	toggle = false;

	item = SideMenus.highLights;

	profileId;

	routerURL$ = this.router.events.pipe(
		tap(data => {
			this.profileService.loadProfileInit();
			this.profile$ = this.profileService.loadProfileData();
		}),
		tap((val: any) => {
			this.profileService.loadProfileApiStatus().subscribe(() => {});
		}),
		filter(val => val instanceof NavigationEnd),
		map((val: any) => ({
			isLearning: val.url.indexOf('/app/learning') > -1,
			isPeople: val.url.indexOf('/app/profile') > -1,
		})),
	);

	constructor(
		private store: Store<any>,
		private router: Router,
		private location: Location,
		public activatedRoute: ActivatedRoute,
		public modalController: ModalController,
		public popoverController: PopoverController,
		private profileService: ProfileService,
		private rudderStack: RudderStack,
	) {
		this.routerURL$
			.pipe(
				filter(({ isLearning }) => !!isLearning),
				first(),
			)
			.subscribe(() => {
				this.learning();
			});
	}

	async ngOnInit() {
		this.profileService.loadProfileData().subscribe(data => {
			this.profileId = data?.data?.id;
		});
	}

	async learning() {
		this.store.dispatch(loadSidebars());
		const arr = this.location.path().split('/');
		const cohortId = Number(arr[3]);
		this.store.dispatch(menuChange({ cohortId }));

		this.sidebar$
			.pipe(
				filter(menu => !menu.isSkeleton),
				first(),
			)
			.subscribe(menu => {
				if (menu.data) {
					if (menu.data[0]) {
						const arr: any = this.location.path().split('/');

						const currentTab = arr[4];

						switch (currentTab) {
							case 'my-schedule':
								this.store.dispatch(
									sidebarMenuChange({ status: SideMenus.mySchedule }),
								);
								this.item = SideMenus.mySchedule;
								break;
							case 'assignments':
								this.store.dispatch(
									sidebarMenuChange({ status: SideMenus.assignments }),
								);
								this.item = SideMenus.assignments;
								break;
							case 'course-info':
								this.store.dispatch(
									sidebarMenuChange({ status: SideMenus.courseInfo }),
								);
								this.item = SideMenus.courseInfo;
								break;
							case 'resources':
								this.store.dispatch(
									sidebarMenuChange({ status: SideMenus.resources }),
								);
								this.item = SideMenus.resources;
								break;
							case 'my-notes':
								this.store.dispatch(
									sidebarMenuChange({ status: SideMenus.myNotes }),
								);
								this.item = SideMenus.myNotes;
								break;
							case 'highLights':
								this.store.dispatch(
									sidebarMenuChange({ status: SideMenus.highLights }),
								);
								this.item = SideMenus.highLights;
								break;
							default:
								this.store.dispatch(
									sidebarMenuChange({ status: SideMenus.highLights }),
								);
								this.item = SideMenus.highLights;
								break;
						}
					}
				}
			});
	}

	selectedItem(item: SideMenus, cohortId: number) {
		this.rudderStack.log({
			type: 'sidemenu_click',
			profileId: this.profileId,
			cohortId: cohortId,
			menuButtonName: item,
		});
		if (item === SideMenus.mySchedule || !item) {
			this.router.navigate([`/app/learning/${cohortId}/my-schedule`]);
		} else if (item === SideMenus.assignments) {
			this.router.navigate([`/app/learning/${cohortId}/assignments`]);
		} else if (item === SideMenus.courseInfo) {
			this.router.navigate([`/app/learning/${cohortId}/course-info`]);
		} else if (item === SideMenus.resources) {
			this.router.navigate([`/app/learning/${cohortId}/resources`]);
		} else if (item === SideMenus.myNotes) {
			this.router.navigate([`/app/learning/${cohortId}/my-notes`]);
		} else if (item === SideMenus.highLights) {
			this.router.navigate([`/app/learning/${cohortId}/highlights`]);
		}

		this.item = item;
		this.store.dispatch(sidebarMenuChange({ status: item }));
	}

	toggleDiv() {
		this.toggle = !this.toggle;
	}

	setSelectedCohort(cohort: { cohortId: number }) {
		this.toggle = false;
		this.store.dispatch(menuChange({ cohortId: cohort.cohortId }));
		this.selectedItem(this.item, cohort.cohortId);
	}

	gotoProfile(profileId) {
		this.rudderStack.log({
			type: 'profile_view_own',
			profileId: this.profileId,
		});
		this.router.navigate([`/app/profile/view/${profileId}`]);
	}

	gotoProfiles() {
		this.router.navigate([`/app/profile`]);
	}

	public async presentCohortSelector(event) {
		this.sidebar$.pipe(first()).subscribe((sidebarData: any) => {
			this.sidebarMenu$.pipe(first()).subscribe(async sideMenuData => {
				const modal = await this.popoverController.create({
					component: CohortSelectorModalComponent,
					cssClass: 'cohort-selector-modal-class',
					event,
					componentProps: {
						sideMenuData,
						sideBarData: sidebarData.data,
					},
				});
				modal.onDidDismiss().then((cohortId: any) => {
					const data = cohortId;
					if (data.data) {
						this.selectedItem(this.item, data.data.cohortId);
					}
				});

				const cohortLength = sidebarData.data.length * 70 + 16;

				modal.style.cssText = `--max-width: 305px;--min-width: 305px;--height: ${
					cohortLength < 370 ? cohortLength : 370
				}px;`;

				return await modal.present();
			});
		});
	}
}
